import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, LogOut, Plus, Settings, Shield, UserIcon, Menu, X } from 'lucide-react';
import SettingsModal from './SettingsModal';
import LanguageSwitcher from './LanguageSwitcher';
import { useTitle } from '../context/TitleContext';
import { useAuth, Permission } from '../context/AuthContext';

const Header = () => {
  const { t } = useTranslation();
  const { title } = useTitle();
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, hasPermission, logout } = useAuth();
  
  // Check if current path is a case details page
  const isCasePage = location.pathname.startsWith('/case/');

  // Check if user has permission to manage templates
  const canManageTemplates = hasPermission('manage_templates');
  // const canAssignTemplates = hasPermission('assign_templates');

  // Navigate back to dashboard preserving state
  const handleBackToDashboard = () => {
    navigate('/');
  };

  // Handle logout
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Helper function to render nav link with permission check
  const renderNavLink = (to: string, label: string, requiredPermission?: Permission, onClick?: () => void) => {
    // Don't render if permission is required but user doesn't have it
    if (requiredPermission && !hasPermission(requiredPermission)) {
      return null;
    }
    
    const isActive = location.pathname === to;
    
    return (
      <Link
        to={to}
        onClick={onClick}
        className={`text-sm font-medium px-3 py-2 rounded-md ${
          isActive
            ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
            : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
        }`}
      >
        {label}
      </Link>
    );
  };

  return (
    <header className="sticky top-0 z-50 bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700 transition-colors duration-200">
      <div className="container mx-auto px-4 py-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-1">
            {isCasePage && (
                <button
                onClick={handleBackToDashboard}
                className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline flex items-center"
                aria-label={t('backToDashboard')}
                >
                <ArrowLeft size={20} className="mr-1" />
                <span className="text-sm font-medium">{t('backToDashboard')}</span>
                </button>
            )}
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">
              {title}
            </h1>
            <div> | </div>
             <button
             className="bg-blue-500 text-white px-1 py-1 rounded"
             onClick={() => window.open('https://primed.tcnw.us/#contact', '_blank')}
             >
             Purchase
             </button>
          </div>
          
          {/* Navigation links - only show if user is logged in */}
          {currentUser && (
            <div className="hidden md:flex items-center space-x-1">
              {renderNavLink('/', t('requestdashboard'))}
              {renderNavLink('/requests/new', t('openNewRequest'), 'new_request')}
              {renderNavLink('/my-templates', t('myTemplates'), 'new_request')}
              {renderNavLink('/recent-activity', t('recentActivity'), 'new_request')}
              {/* Admin Links */}
              {currentUser.role === 'superuser' && (
                <>
                  <div className="h-6 w-px bg-gray-300 dark:bg-gray-600 mx-2"></div>
                  {renderNavLink('/admin/analytics', 'Analytics')}
                  {renderNavLink('/admin/organizations', 'Organizations')}
                  {renderNavLink('/admin/users', 'Users')}
                </>
              )}
              {/* {renderNavLink('/templates', t('templateManagement'), 'manage_templates')} */}
            </div>
          )}
          
          <div className="flex items-center space-x-4">
            <LanguageSwitcher />
            
            {/* Mobile menu button */}
            {currentUser && (
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden p-2 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                aria-label={t('mobileMenu')}
              >
                {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            )}
            
            {currentUser && (
              <div className="relative">
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="flex items-center space-x-2 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                  aria-label={t('userMenu')}
                >
                  <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                    <UserIcon size={16} className="text-blue-600 dark:text-blue-400" />
                  </div>
                  <span className="text-sm font-medium hidden md:block">
                    {currentUser.name}
                  </span>
                  {currentUser.role === 'superuser' && (
                    <Shield size={16} className="text-blue-500" />
                  )}
                </button>
                
                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-2 w-70 py-2 bg-white dark:bg-gray-800 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                    <div className="px-4 py-2 text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                      {t('signedInAs')}
                      <p className="font-medium text-gray-900 dark:text-white">
                        {currentUser.email}
                      </p>
                    </div>
                    
                    {canManageTemplates && (
                    <Link
                      to="/requests/new"
                      className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      <Plus size={16} className="mr-2" />
                      {t('openNewRequest')}
                    </Link>
                     )}
{/* 
                    {canManageTemplates && (
                      <Link
                        to="/templates/new"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <Plus size={16} className="mr-2" />
                        {t('newTemplate')}
                      </Link>

                      
                    )} */}




                    <button
                      onClick={() => {
                        setIsUserMenuOpen(false);
                        setIsSettingsOpen(true);
                      }}
                      className="flex w-full text-left items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      <Settings size={16} className="mr-2" />
                      {t('settings')}
                    </button>
                    
                    <button
                      onClick={handleLogout}
                      className="flex w-full text-left items-center px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                    >
                      <LogOut size={16} className="mr-2" />
                      {t('signOut')}
                    </button>
                  </div>
                )}
              </div>
            )}
            
            {!currentUser && (
              <Link
                to="/login"
                className="flex items-center px-3 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700"
              >
                {t('signIn')}
              </Link>
            )}
          </div>
        </div>
        
        {/* Mobile menu */}
        {currentUser && isMobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex flex-col space-y-2 pt-4">
              {renderNavLink('/', t('requestdashboard'), undefined, () => setIsMobileMenuOpen(false))}
              {renderNavLink('/requests/new', t('openNewRequest'), 'new_request', () => setIsMobileMenuOpen(false))}
              {renderNavLink('/my-templates', t('myTemplates'), 'new_request', () => setIsMobileMenuOpen(false))}
              {renderNavLink('/recent-activity', t('recentActivity'), 'new_request', () => setIsMobileMenuOpen(false))}
              
              {/* Admin Links for Mobile */}
              {currentUser.role === 'superuser' && (
                <>
                  <div className="h-px bg-gray-300 dark:bg-gray-600 my-2"></div>
                  <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase px-3">Admin</div>
                  {renderNavLink('/admin/analytics', 'Analytics', undefined, () => setIsMobileMenuOpen(false))}
                  {renderNavLink('/admin/organizations', 'Organizations', undefined, () => setIsMobileMenuOpen(false))}
                  {renderNavLink('/admin/users', 'Users', undefined, () => setIsMobileMenuOpen(false))}
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {isSettingsOpen && (
        <SettingsModal onClose={() => setIsSettingsOpen(false)} />
      )}
    </header>
  );
};

export default Header;
